import { connect } from 'react-redux';
import { createStructuredSelector, createSelector } from 'reselect';
import Actions from 'Actions';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { userSelector } from 'selectors/user/userSelector';
import SameDayUnlimitedActions from 'actions/SameDayUnlimitedActions';
import basketSelector from 'selectors/basket/basketSelector';
import basketConstants from 'constants/Basket';
import userUtils from 'utils/User';
import urlUtils from 'utils/Url';
import CurrentProductUserSpecificDetailsSelector from 'selectors/page/product/currentProductUserSpecificDetails/currentProductUserSpecificDetailsSelector';
import analyticsConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import getUserSpecificProductDetails from 'services/api/profile/getUserSpecificProductDetails';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import { globalModals as globalModalsUtils } from 'utils/globalModals';
import Empty from 'constants/empty';

const { SDU_SERVICE_INFO } = globalModalsUtils;
const { currentProductUserSpecificDetailsSelector } = CurrentProductUserSpecificDetailsSelector;
const { wrapHOC } = FrameworkUtils;
const { getLocaleResourceFile, getTextFromResource, isUS } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('components/GlobalModals/SDULandingPageModal/locales', 'SDULandingPageModal');

const fields = createSelector(
    userSelector,
    basketSelector,
    currentProductUserSpecificDetailsSelector,
    globalModalsSelector,
    createStructuredSelector({
        title: getTextFromResource(getText, 'title'),
        subscribeToSephora: getTextFromResource(getText, 'subscribeToSephora'),
        subHeader: getTextFromResource(getText, 'subHeader'),
        free30DayTrial: getTextFromResource(getText, 'free30DayTrial'),
        then: getTextFromResource(getText, 'then'),
        annually: getTextFromResource(getText, 'annually'),
        regionalAvailability: getTextFromResource(getText, 'regionalAvailability'),
        addTrialToBasket: getTextFromResource(getText, 'addTrialToBasket'),
        addSubscriptionToBasket: getTextFromResource(getText, 'addSubscriptionToBasket'),
        sameDayUnlimited: getTextFromResource(getText, 'sameDayUnlimited'),
        trial: getTextFromResource(getText, 'trial'),
        subscription: getTextFromResource(getText, 'subscription'),
        alreadyAddedToBasket: getTextFromResource(getText, 'alreadyAddedToBasket'),
        joinForOnly: getTextFromResource(getText, 'joinForOnly'),
        viewBasket: getTextFromResource(getText, 'viewBasket'),
        hasBeenAdded: getTextFromResource(getText, 'hasBeenAdded'),
        activateSubscription: getTextFromResource(getText, 'activateSubscription'),
        activateYour: getTextFromResource(getText, 'activateYour'),
        free30day: getTextFromResource(getText, 'free30day'),
        andSave: getTextFromResource(getText, 'andSave')
    }),
    (user, basket, currentProductUserSpecificDetails, globalModals, textResources) => {
        const { SDUProduct } = basket;
        const { isSDUAddedToBasket } = SDUProduct || {};
        const SDUSku = 'P483900';
        const SM_IMG_SIZE = 50;
        const {
            annually,
            trial,
            subscription,
            alreadyAddedToBasket,
            hasBeenAdded,
            activateSubscription,
            activateYour,
            free30day,
            andSave,
            ...restTextResources
        } = textResources;
        const SDUDetails = currentProductUserSpecificDetails.currentSku || {};
        const isCanada = !isUS();
        const savingsText = `${annually}*`;
        const { BasketType } = basketConstants;
        const basketType = BasketType.Standard;
        const isUserSDUInactive = user?.userSubscriptions?.length > 0 && user?.userSubscriptions[0].status?.startsWith('INACTIVE');
        const isUserTrialEligible = userUtils.isAnonymous() || (isUserSDUInactive && user?.userSubscriptions[0].isTrialEligible);
        const isUserSDUActive = !isUserSDUInactive && !userUtils.isAnonymous();
        const alreadyAddedText = isUserTrialEligible ? `${trial} ${alreadyAddedToBasket}` : `${subscription} ${alreadyAddedToBasket}`;
        const redirectToBasket = () => {
            urlUtils.redirectTo('/basket');
        };
        const redirectToSDUHub = () => {
            urlUtils.redirectTo('/profile/MyAccount/SameDayUnlimited');
        };
        const { isSamedayUnlimitedEnabled } = Sephora.configurationSettings;
        const sduGlobalModalSid = globalModals[SDU_SERVICE_INFO]?.sid || Empty.String;

        const fireAnalytics = skuId => {
            processEvent.process(analyticsConsts.ASYNC_PAGE_LOAD, {
                data: {
                    pageType: analyticsConsts.PAGE_TYPES.PRODUCT,
                    pageName: `${analyticsConsts.PAGE_TYPES.PRODUCT}:${SDUSku}:n/a:*`,
                    eventStrings: [analyticsConsts.Event.PROD_VIEW, analyticsConsts.Event.PRODUCT_VIEW, analyticsConsts.Event.PRODUCT_PAGE_VIEW],
                    productStrings: `;${skuId};;;;eVar26=${skuId}`
                }
            });
        };

        return {
            ...restTextResources,
            SDUDetails,
            isSDUAddedToBasket,
            isUserSDUActive,
            isCanada,
            SM_IMG_SIZE,
            savingsText,
            alreadyAddedText,
            basketType,
            isUserTrialEligible,
            isSamedayUnlimitedEnabled,
            redirectToBasket,
            redirectToSDUHub,
            fireAnalytics,
            SDUSku,
            getUserSpecificProductDetails,
            sduGlobalModalSid
        };
    }
);

const functions = {
    loadContentfulContent: SameDayUnlimitedActions.loadContentfulContent,
    showShippingDeliveryLocationModal: Actions.showShippingDeliveryLocationModal,
    addSDUToBasket: SameDayUnlimitedActions.addSDUToBasket
};

const withSDUProductPageProps = wrapHOC(connect(fields, functions));

export {
    withSDUProductPageProps, fields
};
